import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, idr } from "@/lib/api";
import { ChevronLeft, ChevronRight, Upload, CheckCircle2, User, Briefcase, Award, FileText, CreditCard, ClipboardCheck } from "lucide-react";

const STEPS = [
  { key: "personal", label: "Data Pribadi", icon: User },
  { key: "work", label: "Data Pekerjaan", icon: Briefcase },
  { key: "certification", label: "Data Sertifikasi", icon: Award },
  { key: "documents", label: "Dokumen", icon: FileText },
  { key: "payment", label: "Pembayaran", icon: CreditCard },
  { key: "review", label: "Review", icon: ClipboardCheck },
];

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs font-bold uppercase tracking-widest text-slate-600 mb-1.5">{label}{required && <span className="text-amber-600 ml-1">*</span>}</div>
      {children}
    </label>
  );
}
const inputCls = "w-full px-4 py-3 rounded-lg border-2 border-slate-200 focus:border-emerald-700 outline-none text-sm bg-white";

function FileUpload({ label, required, category, value, onChange }) {
  const [loading, setLoading] = useState(false);
  const handle = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setLoading(true);
    const fd = new FormData(); fd.append("file", f); fd.append("category", category);
    try {
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ id: r.data.id, name: r.data.filename });
      toast.success(`${label} berhasil diupload`);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload gagal"); }
    setLoading(false);
  };
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-widest text-slate-600 mb-1.5">{label}{required && <span className="text-amber-600 ml-1">*</span>}</div>
      <label className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${value ? "border-emerald-500 bg-emerald-50" : "border-slate-300 hover:border-emerald-500 bg-white"}`}>
        {value ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Upload className="w-5 h-5 text-slate-400" />}
        <span className="text-sm text-slate-700 flex-1 truncate">{loading ? "Mengupload..." : value?.name || "Pilih file (PDF/JPG/PNG, maks 5MB)"}</span>
        <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handle} className="hidden" data-testid={`upload-${category}`} />
      </label>
    </div>
  );
}

export default function Register() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const preselectScheme = params.get("skema");
  const [step, setStep] = useState(0);
  const [schemes, setSchemes] = useState([]);
  const [settings, setSettings] = useState(null);
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    personal: { full_name: "", nik: "", birth_place: "", birth_date: "", gender: "Laki-laki", nationality: "Indonesia", address: "", postal_code: "", phone_home: "", phone_office: "", phone_mobile: "", email: "", education: "" },
    work: { company: "", position: "", nationality: "Indonesia", address: "", postal_code: "", phone: "", fax: "", email: "" },
    certification: { scheme_id: preselectScheme || "", purpose: "Sertifikasi", purpose_other: "" },
    documents: { ijazah_file_id: null, experience_file_id: null, ktp_file_id: null, photo_file_id: null },
    payment_proof_file_id: null,
    _files: {}
  });
  useEffect(() => {
    api.get("/schemes").then(r => setSchemes(r.data.sort((a,b)=>a.code.localeCompare(b.code))));
    api.get("/settings").then(r => setSettings(r.data));
  }, []);

  const update = (section, field, val) => setData(d => ({ ...d, [section]: { ...d[section], [field]: val } }));
  const setFile = (section, field, fileMeta) => setData(d => ({
    ...d, [section]: { ...d[section], [field]: fileMeta?.id || null },
    _files: { ...d._files, [`${section}.${field}`]: fileMeta }
  }));

  const validate = () => {
    if (step === 0) {
      const p = data.personal;
      if (!p.full_name || !p.nik || !p.birth_date || !p.email || !p.phone_mobile || !p.address) {
        toast.error("Isi semua field wajib"); return false;
      }
    }
    if (step === 2 && !data.certification.scheme_id) { toast.error("Pilih skema sertifikasi"); return false; }
    if (step === 3) {
      if (!data.documents.ktp_file_id || !data.documents.ijazah_file_id || !data.documents.photo_file_id) {
        toast.error("Lengkapi semua dokumen wajib (Ijazah, KTP, Pas Foto)"); return false;
      }
    }
    return true;
  };

  const submit = async () => {
    if (!agree) { toast.error("Anda harus menyetujui pernyataan"); return; }
    setSubmitting(true);
    try {
      const payload = { personal: data.personal, work: data.work, certification: data.certification, documents: data.documents, payment_proof_file_id: data.payment_proof_file_id };
      const r = await api.post("/registrations", payload);
      toast.success("Pendaftaran berhasil dikirim");
      nav(`/daftar/sukses/${r.data.registration_number}?token=${r.data.access_token}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal submit"); }
    setSubmitting(false);
  };

  const scheme = schemes.find(s => s.id === data.certification.scheme_id);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-3">FR.APL.01</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-emerald-950">Formulir Permohonan Sertifikasi</h1>
        <p className="mt-2 text-slate-600">Biaya pendaftaran: <span className="font-bold text-emerald-950">{idr(settings?.payment?.amount || 1000000)}</span></p>
      </div>

      {/* Stepper */}
      <div className="mb-8 overflow-x-auto">
        <div className="flex items-center gap-1 min-w-max">
          {STEPS.map((s, i) => {
            const done = i < step, active = i === step;
            return (
              <React.Fragment key={s.key}>
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-widest ${active ? "bg-emerald-950 text-amber-300" : done ? "text-emerald-700" : "text-slate-400"}`}>
                  <s.icon className="w-4 h-4" /> {s.label}
                </div>
                {i < STEPS.length - 1 && <div className={`w-6 h-0.5 ${done ? "bg-emerald-700" : "bg-slate-200"}`} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-10">
        {step === 0 && (
          <div data-testid="step-personal" className="grid sm:grid-cols-2 gap-5">
            <Field label="Nama Lengkap" required><input className={inputCls} value={data.personal.full_name} onChange={e => update("personal", "full_name", e.target.value)} data-testid="input-full-name" /></Field>
            <Field label="No. KTP / NIK / Paspor" required><input className={inputCls} value={data.personal.nik} onChange={e => update("personal", "nik", e.target.value)} /></Field>
            <Field label="Tempat Lahir"><input className={inputCls} value={data.personal.birth_place} onChange={e => update("personal", "birth_place", e.target.value)} /></Field>
            <Field label="Tanggal Lahir" required><input type="date" className={inputCls} value={data.personal.birth_date} onChange={e => update("personal", "birth_date", e.target.value)} /></Field>
            <Field label="Jenis Kelamin"><select className={inputCls} value={data.personal.gender} onChange={e => update("personal", "gender", e.target.value)}><option>Laki-laki</option><option>Wanita</option></select></Field>
            <Field label="Kebangsaan"><input className={inputCls} value={data.personal.nationality} onChange={e => update("personal", "nationality", e.target.value)} /></Field>
            <Field label="Alamat Rumah" required><textarea rows="2" className={inputCls} value={data.personal.address} onChange={e => update("personal", "address", e.target.value)} /></Field>
            <Field label="Kode Pos"><input className={inputCls} value={data.personal.postal_code} onChange={e => update("personal", "postal_code", e.target.value)} /></Field>
            <Field label="No. Telepon Rumah"><input className={inputCls} value={data.personal.phone_home} onChange={e => update("personal", "phone_home", e.target.value)} /></Field>
            <Field label="No. Telepon Kantor"><input className={inputCls} value={data.personal.phone_office} onChange={e => update("personal", "phone_office", e.target.value)} /></Field>
            <Field label="No. Handphone" required><input className={inputCls} value={data.personal.phone_mobile} onChange={e => update("personal", "phone_mobile", e.target.value)} /></Field>
            <Field label="Email" required><input type="email" className={inputCls} value={data.personal.email} onChange={e => update("personal", "email", e.target.value)} data-testid="input-email" /></Field>
            <Field label="Kualifikasi Pendidikan"><input className={inputCls} placeholder="Contoh: S1 Teknik" value={data.personal.education} onChange={e => update("personal", "education", e.target.value)} /></Field>
          </div>
        )}
        {step === 1 && (
          <div data-testid="step-work" className="grid sm:grid-cols-2 gap-5">
            <Field label="Nama Institusi / Perusahaan"><input className={inputCls} value={data.work.company} onChange={e => update("work", "company", e.target.value)} /></Field>
            <Field label="Jabatan"><input className={inputCls} value={data.work.position} onChange={e => update("work", "position", e.target.value)} /></Field>
            <Field label="Kebangsaan"><input className={inputCls} value={data.work.nationality} onChange={e => update("work", "nationality", e.target.value)} /></Field>
            <Field label="Alamat Kantor"><textarea rows="2" className={inputCls} value={data.work.address} onChange={e => update("work", "address", e.target.value)} /></Field>
            <Field label="Kode Pos"><input className={inputCls} value={data.work.postal_code} onChange={e => update("work", "postal_code", e.target.value)} /></Field>
            <Field label="Telepon"><input className={inputCls} value={data.work.phone} onChange={e => update("work", "phone", e.target.value)} /></Field>
            <Field label="Fax"><input className={inputCls} value={data.work.fax} onChange={e => update("work", "fax", e.target.value)} /></Field>
            <Field label="Email Kantor"><input type="email" className={inputCls} value={data.work.email} onChange={e => update("work", "email", e.target.value)} /></Field>
          </div>
        )}
        {step === 2 && (
          <div data-testid="step-certification" className="space-y-5">
            <Field label="Pilih Skema Sertifikasi" required>
              <select className={inputCls} value={data.certification.scheme_id} onChange={e => update("certification", "scheme_id", e.target.value)} data-testid="select-scheme">
                <option value="">-- Pilih skema --</option>
                {schemes.map(s => <option key={s.id} value={s.id}>{s.code}. {s.title}</option>)}
              </select>
            </Field>
            <Field label="Tujuan Asesmen" required>
              <select className={inputCls} value={data.certification.purpose} onChange={e => update("certification", "purpose", e.target.value)}>
                <option>Sertifikasi</option><option>Pengakuan Kompetensi Terkini (PKT)</option><option>Rekognisi Pembelajaran Lampau</option><option>Lainnya</option>
              </select>
            </Field>
            {data.certification.purpose === "Lainnya" && <Field label="Jelaskan tujuan lainnya"><textarea rows="3" className={inputCls} value={data.certification.purpose_other} onChange={e => update("certification", "purpose_other", e.target.value)} /></Field>}
          </div>
        )}
        {step === 3 && (
          <div data-testid="step-documents" className="space-y-5">
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">Format PDF/JPG/PNG, maksimal 5MB per file.</div>
            <FileUpload label="Fotokopi Ijazah (minimal SMA)" required category="ijazah" value={data._files["documents.ijazah_file_id"]} onChange={f => setFile("documents", "ijazah_file_id", f)} />
            <FileUpload label="Sertifikat Pelatihan / Surat Pengalaman Kerja (min. 2 tahun)" category="experience" value={data._files["documents.experience_file_id"]} onChange={f => setFile("documents", "experience_file_id", f)} />
            <FileUpload label="Foto Copy Identitas Diri / KTP" required category="ktp" value={data._files["documents.ktp_file_id"]} onChange={f => setFile("documents", "ktp_file_id", f)} />
            <FileUpload label="Pas Foto Berwarna 3x4" required category="photo" value={data._files["documents.photo_file_id"]} onChange={f => setFile("documents", "photo_file_id", f)} />
          </div>
        )}
        {step === 4 && (
          <div data-testid="step-payment" className="space-y-6">
            <div className="rounded-2xl bg-emerald-950 text-white p-8">
              <div className="text-xs uppercase tracking-widest text-amber-300 font-bold mb-2">Biaya Sertifikasi</div>
              <div className="font-display text-4xl font-extrabold gold-gradient-text">{idr(settings?.payment?.amount || 1000000)}</div>
              <div className="mt-6 space-y-2 text-sm">
                <div><span className="text-white/60">Bank:</span> <span className="font-semibold">{settings?.payment?.bank_name}</span></div>
                <div><span className="text-white/60">No. Rekening:</span> <span className="font-mono font-semibold">{settings?.payment?.account_number}</span></div>
                <div><span className="text-white/60">Atas Nama:</span> <span className="font-semibold">{settings?.payment?.account_holder}</span></div>
              </div>
              <p className="mt-6 text-sm text-white/80 leading-relaxed">{settings?.payment?.instructions}</p>
            </div>
            <FileUpload label="Upload Bukti Pembayaran" required category="payment" value={data._files["payment_proof_file_id"]} onChange={f => { setData(d => ({ ...d, payment_proof_file_id: f?.id || null, _files: { ...d._files, "payment_proof_file_id": f } })); }} />
          </div>
        )}
        {step === 5 && (
          <div data-testid="step-review" className="space-y-6">
            <h3 className="font-display text-xl font-extrabold text-emerald-950">Review Data Pendaftaran</h3>
            <ReviewBlock title="Data Pribadi" data={data.personal} />
            <ReviewBlock title="Data Pekerjaan" data={data.work} />
            <ReviewBlock title="Data Sertifikasi" data={{ Skema: scheme ? `${scheme.code}. ${scheme.title}` : "-", Tujuan: data.certification.purpose }} />
            <ReviewBlock title="Dokumen" data={{
              Ijazah: data._files["documents.ijazah_file_id"]?.name || "-",
              Pengalaman: data._files["documents.experience_file_id"]?.name || "-",
              KTP: data._files["documents.ktp_file_id"]?.name || "-",
              Pasfoto: data._files["documents.photo_file_id"]?.name || "-",
              "Bukti Bayar": data._files["payment_proof_file_id"]?.name || "-",
            }} />
            <label className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
              <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} data-testid="agree-checkbox" className="mt-1 w-5 h-5 accent-emerald-700" />
              <span className="text-sm text-amber-900">Saya menyatakan bahwa data yang saya berikan adalah benar dan dapat dipertanggungjawabkan.</span>
            </label>
          </div>
        )}

        <div className="mt-10 flex justify-between">
          <button disabled={step === 0} onClick={() => setStep(s => s - 1)} className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border-2 border-slate-200 text-slate-700 font-bold text-sm disabled:opacity-40 hover:bg-slate-50">
            <ChevronLeft className="w-4 h-4" /> Kembali
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={() => { if (validate()) setStep(s => s + 1); }} data-testid="next-step-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-emerald-950 text-white font-bold text-sm hover:bg-emerald-800">
              Lanjut <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button onClick={submit} disabled={submitting || !agree} data-testid="submit-registration-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg gold-gradient text-emerald-950 font-bold text-sm disabled:opacity-40">
              {submitting ? "Mengirim..." : "Submit Pendaftaran"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewBlock({ title, data }) {
  return (
    <div className="border border-slate-200 rounded-xl p-5">
      <div className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-3">{title}</div>
      <dl className="grid sm:grid-cols-2 gap-3 text-sm">
        {Object.entries(data).filter(([k]) => !k.startsWith("_")).map(([k, v]) => (
          <div key={k}><dt className="text-slate-500 text-xs capitalize">{k.replace(/_/g, " ")}</dt><dd className="text-slate-900 mt-0.5">{String(v || "-")}</dd></div>
        ))}
      </dl>
    </div>
  );
}
